import { createSelector } from 'reselect';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

const LOG_LOCATION = 'issue.selectors.createSelectorWithLogging';

// Action buffer for tracking recent actions to help debug selector errors
const actionBuffer: string[] = [];
autoBeforeEachTest(() => {
	actionBuffer.length = 0;
});
const MAX_ACTION_BUFFER_SIZE = 50;

/**
 * Push an action type to the action buffer for debugging purposes.
 * Used by the error logging middleware to track recent actions.
 */
export const pushToActionBuffer = (type: string): void => {
	if (actionBuffer.length >= MAX_ACTION_BUFFER_SIZE) {
		actionBuffer.shift();
	}
	const timestamp = new Date().toISOString();
	actionBuffer.push(`${timestamp} ${type}`);
};

/**
 * Get the current action buffer for debugging purposes.
 * Used by selector error logging to provide context about recent actions.
 */
export const getActionBuffer = (): string[] => actionBuffer;

/**
 * Wrapper around createSelector that adds error logging when selectors fail.
 */
export function createSelectorWithLogging<State, Input, Output>(
	// note:-createSelector has overloaded types, I am using the one we need for locale selector, We can change it if we need to use this wrapper universally.
	inputSelector: (state: State) => Input,
	resultFunc: (input: Input) => Output,
): (state: State) => Output {
	const selector = createSelector(inputSelector, resultFunc);
	return (state: State): Output => {
		try {
			return selector(state);
		} catch (error) {
			const recentActions = getActionBuffer();
			const errorMessage = error instanceof Error ? error.message : String(error);
			const actionBufferSummary = recentActions.length > 0 ? recentActions.join('; ') : '';

			log.safeErrorWithoutCustomerData(LOG_LOCATION, errorMessage, {
				errorMessage,
				actionBufferSummary,
				selectorError: true,
			});

			throw error;
		}
	};
}
